import React, { useMemo, useState } from 'react';
import { SearchIcon, ShieldAlertIcon, ShieldCheckIcon, ClockIcon } from 'lucide-react';
type DocState = 'Verificado' | 'Pendiente' | 'Por vencer';
type Doc = {
  name: string;
  scope: 'Transportista' | 'Chofer' | 'Camión' | 'Acoplado';
  state: DocState;
  frequency: string;
  invalidating: boolean;
};
type Unit = {
  plate: string;
  internal: string;
  type: string;
  carrier: string;
  status: 'Habilitada' | 'Observada' | 'Bloqueada';
  docs: Doc[];
};
const UNITS: Unit[] = [{
  plate: 'AB 123 CD',
  internal: 'INT-014',
  type: 'Chasis + acoplado',
  carrier: 'Flota propia',
  status: 'Habilitada',
  docs: [{
    name: 'Seguro obligatorio',
    scope: 'Camión',
    state: 'Verificado',
    frequency: 'Anual',
    invalidating: true
  }, {
    name: 'RTO / VTV',
    scope: 'Camión',
    state: 'Verificado',
    frequency: 'Semestral',
    invalidating: true
  }, {
    name: 'Licencia nacional de transporte',
    scope: 'Transportista',
    state: 'Verificado',
    frequency: 'Anual',
    invalidating: true
  }, {
    name: 'Licencia de conducir profesional',
    scope: 'Chofer',
    state: 'Verificado',
    frequency: 'Anual',
    invalidating: true
  }, {
    name: 'Checklist de unidad',
    scope: 'Camión',
    state: 'Verificado',
    frequency: 'Mensual',
    invalidating: false
  }]
}, {
  plate: 'AC 456 EF',
  internal: 'INT-027',
  type: 'Batea 35 TN',
  carrier: 'Red 3PL',
  status: 'Observada',
  docs: [{
    name: 'Seguro obligatorio',
    scope: 'Camión',
    state: 'Verificado',
    frequency: 'Anual',
    invalidating: true
  }, {
    name: 'RTO / VTV acoplado',
    scope: 'Acoplado',
    state: 'Por vencer',
    frequency: 'Semestral',
    invalidating: true
  }, {
    name: 'Control de extintores',
    scope: 'Camión',
    state: 'Verificado',
    frequency: 'Anual',
    invalidating: false
  }, {
    name: 'Control de neumáticos',
    scope: 'Camión',
    state: 'Pendiente',
    frequency: 'Mensual',
    invalidating: false
  }, {
    name: 'Capacitación manejo defensivo',
    scope: 'Chofer',
    state: 'Verificado',
    frequency: 'Anual',
    invalidating: false
  }]
}, {
  plate: 'AD 789 GH',
  internal: 'INT-041',
  type: 'Liviano 500 kg',
  carrier: 'Transportista C',
  status: 'Bloqueada',
  docs: [{
    name: 'Seguro obligatorio',
    scope: 'Camión',
    state: 'Pendiente',
    frequency: 'Anual',
    invalidating: true
  }, {
    name: 'Licencia de conducir profesional',
    scope: 'Chofer',
    state: 'Verificado',
    frequency: 'Anual',
    invalidating: true
  }, {
    name: 'Constancia de inscripción',
    scope: 'Transportista',
    state: 'Verificado',
    frequency: 'Anual',
    invalidating: false
  }, {
    name: 'Checklist de unidad',
    scope: 'Camión',
    state: 'Por vencer',
    frequency: 'Mensual',
    invalidating: false
  }]
}];
const STATUS_STYLES: Record<Unit['status'], string> = {
  Habilitada: 'bg-brand-50 text-brand-700 border-brand-200',
  Observada: 'bg-accent-300/20 text-accent-600 border-accent-300',
  Bloqueada: 'bg-mist-100 text-graphite-700 border-mist-300'
};
const DOC_ICON: Record<DocState, React.ComponentType<{
  className?: string;
}>> = {
  Verificado: ShieldCheckIcon,
  Pendiente: ShieldAlertIcon,
  'Por vencer': ClockIcon
};
const DOC_COLOR: Record<DocState, string> = {
  Verificado: 'text-brand-600',
  Pendiente: 'text-graphite-500',
  'Por vencer': 'text-accent-600'
};
export function DocumentPlatform() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(UNITS[1].plate);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return UNITS;
    return UNITS.filter(u => u.plate.toLowerCase().includes(q) || u.internal.toLowerCase().includes(q) || u.carrier.toLowerCase().includes(q));
  }, [query]);
  const unit = UNITS.find(u => u.plate === selected) ?? UNITS[0];
  const pending = unit.docs.filter(d => d.state !== 'Verificado');
  return <div className="overflow-hidden border border-mist-200 bg-white shadow-xl" data-id="element-171" data-mp="3sqnz73uc1mf0">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-mist-200 bg-mist-50 px-4 py-3" data-id="element-172" data-mp="1mh9gapjwmxz">
        <div className="flex items-center gap-3" data-id="element-173" data-mp="gxfhokj6hjm6">
          <span className="font-display text-sm font-semibold text-graphite-900" data-id="element-174" data-mp="c4ckm2f6zs18">Plataforma de control documental</span>
          <span className="border border-accent-300 bg-accent-300/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-accent-600" data-id="element-175" data-mp="83sqz8ttv6ju">
            Demo
          </span>
        </div>
        <label className="flex min-w-[240px] flex-1 items-center gap-2 border border-mist-300 bg-white px-3 py-2 md:max-w-xs" data-id="element-176" data-mp="zsvugmdw4cvf">
          <SearchIcon className="h-4 w-4 shrink-0 text-graphite-500" aria-hidden="true" data-id="element-177" data-mp="vqhb816b7b3m" />
          <span className="sr-only" data-id="element-178" data-mp="qrnwk5fade1v">Buscar por patente, número interno o transportista</span>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Patente o número interno" className="w-full bg-transparent text-sm text-graphite-800 placeholder:text-graphite-500/70 focus:outline-none" data-id="element-179" data-mp="ltw49aaqbpx5" />
        </label>
      </div>

      <div className="grid divide-mist-200 lg:grid-cols-[300px_1fr] lg:divide-x" data-id="element-180" data-mp="2wij4tzyslzsf">
        <div className="max-h-[420px] overflow-y-auto" data-id="element-181" data-mp="31gaxwxb6bry7">
          <ul className="divide-y divide-mist-200" data-id="element-182" data-mp="36ecgzi8lnr9o">
            {filtered.map(u => <li key={u.plate} data-id="element-183" data-mp="3b5cdimdt47bn">
                <button type="button" onClick={() => setSelected(u.plate)} aria-current={u.plate === selected} className={`w-full px-4 py-4 text-left transition-colors duration-150 ${u.plate === selected ? 'bg-brand-50' : 'hover:bg-mist-50'}`} data-id="element-184" data-mp="3b6nqnct56rff">
                  <span className="flex items-center justify-between gap-2" data-id="element-185" data-mp="3u5043ar36ngt">
                    <span className="font-display text-sm font-bold tabular-nums text-graphite-900" data-id="element-186" data-mp="1urcgbj6civs3">{u.plate}</span>
                    <span className={`border px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLES[u.status]}`} data-id="element-187" data-mp="1zq5y3nso18y2">{u.status}</span>
                  </span>
                  <span className="mt-1 block text-xs text-graphite-500" data-id="element-188" data-mp="1rpzw8w1wt7gq">
                    {u.internal} · {u.type}
                  </span>
                  <span className="mt-0.5 block text-xs text-graphite-500" data-id="element-189" data-mp="21nntbsmzmr22">{u.carrier}</span>
                </button>
              </li>)}
            {filtered.length === 0 && <li className="px-4 py-8 text-center text-xs text-graphite-500" data-id="element-190" data-mp="2aolu8224qigf">
                Sin resultados para “{query}”. Pruebe con otra patente o número interno.
              </li>}
          </ul>
        </div>

        <div className="p-4 md:p-6" data-id="element-191" data-mp="2keoos6owg4gv">
          <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-192" data-mp="2kdn9fq0uovtc">
            <div data-id="element-193" data-mp="2fgn3df403t28">
              <h3 className="font-display text-xl font-bold tracking-tight text-graphite-900" data-id="element-194" data-mp="2fg3wqujswr3i">{unit.plate}</h3>
              <p className="mt-1 text-sm text-graphite-500" data-id="element-195" data-mp="2td0p50fkigmi">
                {unit.internal} · {unit.type} · {unit.carrier}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-6 text-center" data-id="element-196" data-mp="382vbsbyw1llz">
              <div data-id="element-197" data-mp="3d0mzsuwewnkw">
                <p className="font-display text-lg font-bold tabular-nums text-graphite-900" data-id="element-198" data-mp="3d157cnoiyo84">{unit.docs.length}</p>
                <p className="text-[10px] uppercase tracking-[0.12em] text-graphite-500" data-id="element-199" data-mp="2z0c8jqr6x6z7">Requeridos</p>
              </div>
              <div data-id="element-200" data-mp="33ydtkh86fgdb">
                <p className="font-display text-lg font-bold tabular-nums text-brand-600" data-id="element-201" data-mp="3421nvilg46t3">
                  {unit.docs.length - pending.length}
                </p>
                <p className="text-[10px] uppercase tracking-[0.12em] text-graphite-500" data-id="element-202" data-mp="2u3luta2uyngy">Verificados</p>
              </div>
              <div data-id="element-203" data-mp="2g8jio57f41vp">
                <p className="font-display text-lg font-bold tabular-nums text-accent-600" data-id="element-204" data-mp="2kc7r0q3vj1i0">{pending.length}</p>
                <p className="text-[10px] uppercase tracking-[0.12em] text-graphite-500" data-id="element-205" data-mp="26bwwnd3cv4s3">Pendientes</p>
              </div>
            </div>
          </div>

          <table className="mt-6 w-full text-left text-xs" data-id="element-206" data-mp="2barn917kdxmr">
            <caption className="sr-only" data-id="element-207" data-mp="2qtswl69mxi6x">Estado documental de la unidad seleccionada</caption>
            <thead data-id="element-208" data-mp="2lu81j6xl5tzh">
              <tr className="text-[10px] uppercase tracking-[0.12em] text-graphite-500" data-id="element-209" data-mp="2lvrg8t3lfwz5">
                <th scope="col" className="pb-3 font-semibold" data-id="element-210" data-mp="2hrtj2nxtww8q">Documento</th>
                <th scope="col" className="hidden pb-3 font-semibold sm:table-cell" data-id="element-211" data-mp="2cub7epsfg2x1">Alcance</th>
                <th scope="col" className="pb-3 font-semibold" data-id="element-212" data-mp="27v8c3x291b29">Estado</th>
                <th scope="col" className="hidden pb-3 font-semibold md:table-cell" data-id="element-213" data-mp="27wjhg1bceqvh">Frecuencia</th>
                <th scope="col" className="pb-3 font-semibold" data-id="element-214" data-mp="2325dvbmj1dfq">Invalidante</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-mist-200" data-id="element-215" data-mp="1u3k1xzbk8wnx">
              {unit.docs.map(doc => {
              const Icon = DOC_ICON[doc.state];
              return <tr key={doc.name} data-id="element-216" data-mp="1p61p3xuf9xan">
                    <td className="py-3 pr-3 font-medium text-graphite-900" data-id="element-217" data-mp="1k4ug5vgjms2e">{doc.name}</td>
                    <td className="hidden py-3 pr-3 text-graphite-500 sm:table-cell" data-id="element-218" data-mp="3vszandxuidcz">{doc.scope}</td>
                    <td className="py-3 pr-3" data-id="element-219" data-mp="4ln2tpiurc9q">
                      <span className={`inline-flex items-center gap-1.5 font-semibold ${DOC_COLOR[doc.state]}`} data-id="element-220" data-mp="4kbqh1bccw70">
                        <Icon className="h-3.5 w-3.5" data-id="element-221" data-mp="9jevilrbn2ez" />
                        {doc.state}
                      </span>
                    </td>
                    <td className="hidden py-3 pr-3 text-graphite-500 md:table-cell" data-id="element-222" data-mp="3d2rdrdkqoj58">{doc.frequency}</td>
                    <td className="py-3 text-graphite-500" data-id="element-223" data-mp="3i24acq1ex4l0">{doc.invalidating ? 'Sí' : 'No'}</td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="border-t border-mist-200 bg-mist-50 px-4 py-3 text-[11px] leading-relaxed text-graphite-500" data-id="element-224" data-mp="3qu69mkzqwetp">
        Interfaz de referencia con datos de demostración. Los documentos requeridos, frecuencias y criterios invalidantes se
        configuran según la operación y la normativa aplicable a cada cliente.
      </p>
    </div>;
}